import { useCallback, useEffect, useRef, useState } from 'react';
import { compositionFluency } from './baseline';
import { wpm as wpmFn } from './stats';
import {
  isPrintableTypingKey,
  shouldIgnoreKeyEvent,
} from './typingKey';

export interface SkrivelegDraftStats {
  wpm: number;
  fluency: number;
  correctChars: number;
  totalKeystrokes: number;
  durationMs: number;
}

export interface UseSkrivelegDraftInputState {
  draft: string;
  stats: SkrivelegDraftStats;
  clearDraft: () => void;
}

export function useSkrivelegDraftInput(
  enabled: boolean,
  onSend: (text: string, stats: SkrivelegDraftStats) => void,
): UseSkrivelegDraftInputState {
  const onSendRef = useRef(onSend);
  useEffect(() => {
    onSendRef.current = onSend;
  }, [onSend]);

  const [draft, setDraft] = useState('');
  const [tick, setTick] = useState(0);
  const stateRef = useRef({
    draft: '',
    startedAtMs: 0,
    correctChars: 0,
    totalKeystrokes: 0,
    backspaces: 0,
  });

  const forceRerender = useCallback(() => setTick((t) => t + 1), []);

  const clearDraft = useCallback(() => {
    stateRef.current.draft = '';
    setDraft('');
    forceRerender();
  }, [forceRerender]);

  const sendDraft = useCallback(() => {
    const s = stateRef.current;
    const text = s.draft.trim();
    if (!text || !enabled) return;
    const durationMs =
      s.startedAtMs === 0 ? 0 : Math.max(1, Date.now() - s.startedAtMs);
    const stats: SkrivelegDraftStats = {
      wpm: wpmFn(s.correctChars, durationMs),
      fluency: compositionFluency(s.totalKeystrokes, s.backspaces),
      correctChars: s.correctChars,
      totalKeystrokes: s.totalKeystrokes,
      durationMs,
    };
    onSendRef.current(text, stats);
    s.draft = '';
    setDraft('');
    forceRerender();
  }, [enabled, forceRerender]);

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      const s = stateRef.current;
      if (shouldIgnoreKeyEvent(e)) return;

      if (e.key === 'Escape') return;

      if (e.key === 'Enter') {
        e.preventDefault();
        sendDraft();
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        if (s.draft.length === 0) return;
        s.backspaces += 1;
        s.totalKeystrokes += 1;
        s.draft = s.draft.slice(0, -1);
        s.correctChars = Math.max(0, s.correctChars - 1);
        setDraft(s.draft);
        forceRerender();
        return;
      }

      if (e.key === 'Dead') return;
      if (!isPrintableTypingKey(e.key)) return;
      e.preventDefault();

      if (s.startedAtMs === 0) s.startedAtMs = Date.now();
      s.totalKeystrokes += 1;
      s.correctChars += 1;
      s.draft += e.key;
      setDraft(s.draft);
      forceRerender();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, sendDraft, forceRerender]);

  void tick;
  const s = stateRef.current;
  const durationMs =
    s.startedAtMs === 0 ? 0 : Math.max(1, Date.now() - s.startedAtMs);

  return {
    draft,
    stats: {
      wpm: wpmFn(s.correctChars, durationMs),
      fluency: compositionFluency(s.totalKeystrokes, s.backspaces),
      correctChars: s.correctChars,
      totalKeystrokes: s.totalKeystrokes,
      durationMs,
    },
    clearDraft,
  };
}
